import { getPackDeliveryAddress } from 'helpers/addressHelper';
import moment from 'moment';

export const DEFAULT_BOX_SIZES = {
  Occasional: 30,
  'Part Time': 30,
  'Full Time': 30,
  Monthly: 3,
};

const LENS_USAGE_TYPE_MAP = {
  Occasional: 'Daily Disposable',
  Monthly: 'Monthly Reusable',
  'Part Time': 'Daily Disposable',
  'Full Time': 'Daily Disposable',
};

export const findNextAndPrevPack = (packs, today) => packs.reduce((accumulator, pack) => {
  if (
    ['Pending', 'Cancelled', 'Processing', 'Dispatched'].includes(pack.status)
      && pack.delivery_date > today
  ) {
    if (
      !accumulator.nextPack
        || accumulator.nextPack.delivery_date > pack.delivery_date
    ) {
      accumulator.nextPack = pack;
    }

    if (
      ['Pending', 'Processing', 'Dispatched'].includes(pack.status)
        && (!accumulator.nextDeliverablePack
          || accumulator.nextDeliverablePack.delivery_date > pack.delivery_date)
    ) {
      accumulator.nextDeliverablePack = pack;
    }

    if (pack.status === 'Pending'
      && !pack.payments.find((p) => p.status !== 'Pending')
      && (!accumulator.nextUnpaidPack
      || accumulator.nextUnpaidPack.delivery_date > pack.delivery_date)
    ) {
      accumulator.nextUnpaidPack = pack;
    }

    if (accumulator.nextDeliverablePack
      && !accumulator.nextDeliverablePack.packChanges
      && pack.cart_id !== accumulator.nextDeliverablePack.cart_id) {
      accumulator.nextDeliverablePack.packChanges = pack;
    }
  }

  if (
    pack.delivery_date <= today
      && (!accumulator.previousPack
        || accumulator.previousPack.delivery_date < pack.delivery_date)
      && !['Pending', 'Processing'].includes(pack.status)
  ) {
    accumulator.previousPack = pack;
  }

  return accumulator;
}, {});

const mapCart = (cart) => {
  const leftLens = cart.left && { ...cart.left, lensId: 'left' };
  const rightLens = cart.right && { ...cart.right, lensId: 'right' };
  const hasSolution = cart.extras && cart.extras.solution;

  const numberOfEyes = [cart.left, cart.right].filter((c) => !!c).length;
  const eitherLens = [cart.left, cart.right].find((lens) => !!lens);
  const boxSize = eitherLens.box_size || DEFAULT_BOX_SIZES[eitherLens.usage];
  const { quantity } = eitherLens;

  const items = [leftLens, rightLens]
    .filter((lens) => !!lens)
    .map((lens) => ({
      styleNumber: lens.lens_detail.clfit.style_number,
      title: lens.product_description,
      subTitle: LENS_USAGE_TYPE_MAP[lens.usage],
      type: lens.usage,
      lensId: lens.lensId,
      kind: 'lens',
      boxSize: lens.box_size || DEFAULT_BOX_SIZES[lens.usage],
    }))
    .map((lens) => ({
      ...lens,
      url: `https://d1u18ka6p421dn.cloudfront.net/cl-images-gb-en/${lens.styleNumber}-${lens.boxSize}-600x480.jpg`,
    }));

  if (hasSolution) {
    items.push({
      kind: 'solution',
      productId: cart.extras.solution.product_id,
      description: cart.extras.solution.product_description,
      sku: cart.extras.solution.solution_detail.solution_sku,
      title: cart.extras.solution.product_description,
      subTitle: 'Solution',
      url: `https://images.specsavers.com/solutions-ie-en/${cart.extras.solution.solution_detail.solution_sku}.jpg`,
    });
  }

  return {
    items: [...items],
    packSize: {
      boxSize,
      quantity,
      numberOfEyes,
      usage: eitherLens.usage,
    },
    hasSolution,
  };
};

export const mapPack = (pack, subscription, account, store, applyRecursion = true) => pack && {
  id: pack.id,
  status: pack.status,
  deliveryDate: pack.delivery_date,
  availableActions: pack.available_actions,
  payments: pack.payments.map((p) => ({
    date: p.payment_date,
    status: p.representation_date && moment(p.representation_date).isAfter() ? 'Pending' : p.status,
    amount: p.gross_amount,
    currency: subscription.currency,
    isNextPayment: p.payment_date === subscription.next_payment,
  })),
  deliveryAddress: getPackDeliveryAddress(pack, account, store),
  product: mapCart(subscription.carts.find((c) => c.id === pack.cart_id)),
  packChanges: pack.packChanges && mapPack(pack.packChanges, subscription, account, store),
  storeUrl: store.url,
  nextPack: (applyRecursion && mapPack(pack.nextPack, subscription, account, store, false))
    || undefined,
  previousPack: (applyRecursion && mapPack(pack.previousPack, subscription, account, store, false))
    || undefined,
};

export const mapSubscription = (subscription) => ({
  price: {
    price: subscription.next_payment_amount,
    currency: subscription.currency,
    frequency: 'mo.',
  },
  currentProduct: mapCart(subscription.cart),
});

export const mapTopups = (subscription) => ({
  topupDate: subscription.next_delivery,
  topupSize: mapCart(subscription.cart).packSize,
});
